/**
 * Maps Firebase authentication error codes to user-friendly messages
 * @param {Error} error - The Firebase auth error
 * @returns {string} - User-friendly error message
 */
export const getFirebaseErrorMessage = (error) => {
  if (!error || !error.code) {
    return "An unexpected error occurred. Please try again.";
  }

  const errorMessages = {
    // Authentication errors
    "auth/invalid-email": "Invalid email address. Please check and try again.",
    "auth/user-disabled": "This account has been disabled. Please contact support.",
    "auth/user-not-found": "No account found with this email address.",
    "auth/wrong-password": "Incorrect password. Please try again.",
    "auth/invalid-password": "Password must be at least 6 characters long.",
    "auth/weak-password": "Password is too weak. It must contain uppercase, lowercase, numbers, and symbols.",
    "auth/email-already-in-use": "An account with this email already exists.",
    "auth/account-exists-with-different-credential": "An account already exists with this email using a different login method.",
    "auth/operation-not-allowed": "This authentication method is not enabled. Please contact support.",
    "auth/invalid-credential": "Invalid email or password.",
    "auth/invalid-verification-code": "Invalid verification code.",
    "auth/invalid-verification-id": "Invalid verification ID.",
    "auth/missing-verification-code": "Verification code is required.",
    "auth/missing-verification-id": "Verification ID is required.",
    "auth/quota-exceeded": "Too many login attempts. Please try again later.",
    "auth/user-token-expired": "Your session has expired. Please log in again.",
    "auth/too-many-requests": "Too many failed login attempts. Please try again later.",
    "auth/network-request-failed": "Network error. Please check your internet connection and try again.",
    "auth/internal-error": "Internal authentication error. Please try again.",
    "auth/invalid-api-key": "API key error. Please contact support.",
    "auth/app-not-authorized": "Application is not authorized. Please contact support.",

    // Password reset errors
    "auth/missing-email": "Email address is required for password reset.",
    "auth/invalid-sender": "The sender email is invalid.",
    "auth/invalid-recipient-email": "The recipient email is invalid.",

    // Update profile errors
    "auth/requires-recent-login": "This operation requires a recent login. Please log out and log in again.",
    "auth/no-current-user": "No user is currently logged in.",
  };

  return errorMessages[error.code] || `Error: ${error.message || error.code}`;
};

/**
 * Maps backend API error responses to user-friendly messages
 * @param {number} statusCode - HTTP status code
 * @param {string} errorText - Error response text from server
 * @returns {string} - User-friendly error message
 */
export const getBackendErrorMessage = (statusCode, errorText) => {
  const errorMessages = {
    400: "Invalid request. Please check your information and try again.",
    401: "Unauthorized. Please log in and try again.",
    403: "Access denied. You do not have permission to perform this action.",
    404: "The requested resource was not found.",
    409: "A conflict occurred. This account may already exist.",
    422: "Invalid data provided. Please check your information and try again.",
    429: "Too many requests. Please wait a moment and try again.",
    500: "Server error. Please try again later.",
    502: "Bad gateway. Please try again later.",
    503: "Service unavailable. Please try again later.",
  };

  const message = errorMessages[statusCode] || `Error (${statusCode}): ${errorText || "Unknown error"}`;
  return message;
};

/**
 * Comprehensive error handler for Firebase operations
 * @param {Error} error - The error object
 * @param {string} type - Type of error ('firebase' or 'backend')
 * @returns {string} - User-friendly error message
 */
export const handleAuthError = (error, type = "firebase") => {
  if (type === "backend") {
    // For backend errors, error might be structured differently
    if (error.statusCode && error.errorText) {
      return getBackendErrorMessage(error.statusCode, error.errorText);
    }
  }

  return getFirebaseErrorMessage(error);
};
